import Image from 'next/image'
import React, { FC, ReactElement } from 'react'
import styles from './CollectionIcon.module.css'
import { Collection } from '@/utils/types/collections'
import { Inscription } from '@/utils/types/inscription'
import InscriptionContent from '@/components/inscription-content'
import classNames from 'classnames'
import { COLLECTION_SLUGS, ORD_CONTENT_URL } from '@/utils/constants'
// Shared with the social feed's market pills and the market picker, so a
// collection wears the same mark wherever it appears.
import { COLLECTION_ICON_OVERRIDES as ICON_OVERRIDES, collectionThumbnailUrl } from '@/utils/collection-icon-url'
import isRune from '@/utils/is-rune'


const CollectionIcon: FC<{
	className?: string
	imageClassName?: string
	inscriptionClassName?: string
	collection: Collection
	thumbnail?: boolean
	/** First paint candidate. Preloads and skips lazy. */
	priority?: boolean
	symbol?: string
	style?: React.CSSProperties
}> = (props): ReactElement => {
	const { name, icon_inscription } = props?.collection || {}
	const [imgError, setImgError] = React.useState(false)

	const slug = props?.collection?.slug
	const override = slug ? ICON_OVERRIDES[slug] : undefined
	const imageSrc = override
		? override
		: COLLECTION_SLUGS.includes(slug)
		? `/collections/${slug}/icon.png`
		: props?.collection?.icon?.replace('turbo', 'cdn')

	const isTextRune =
		icon_inscription?.content_type?.includes('text') && isRune(props?.collection?.slug)

	const isImageOverrideRune = isRune(props?.collection?.slug) && imageSrc
	const hasSlugOverride = Boolean(override)
	// A rune's icon is its etching inscription. Sales rows carry it with
	// `content_type: 'no_content_type'` (the etching is not in our inscription
	// index), which sent the renderer to the CDN preview — a 404 and a broken
	// tile — while ord serves the image itself. Load it from ord directly.
	const iconContentTypeRaw = icon_inscription?.content_type || ''
	const isRuneEtchingIcon =
		isRune(props?.collection?.slug) &&
		!!icon_inscription?.id &&
		!imageSrc &&
		!hasSlugOverride &&
		(iconContentTypeRaw === '' ||
			iconContentTypeRaw === 'no_content_type' ||
			iconContentTypeRaw.startsWith('image/'))

	const iconContentType = icon_inscription?.content_type || ''
	const useContentImage = icon_inscription?.id && !isTextRune && !isImageOverrideRune && !hasSlugOverride &&
		(iconContentType.includes('application/octet-stream') || isRuneEtchingIcon)
	const imageErrorKey = `${icon_inscription?.id || ''}|${iconContentType}|${imageSrc || ''}`

	React.useEffect(() => {
		setImgError(false)
	}, [imageErrorKey])

 // The moving homepage rail needs small rasters. Full content URLs are
 // multi-megabyte and were gated on opacity-0 until hydration, so they
 // blocked LCP. Paint the preview immediately with reserved dimensions.
 const thumbnailSrc = collectionThumbnailUrl(
  imageSrc,
  isRuneEtchingIcon ? undefined : icon_inscription?.id,
  { contentType: icon_inscription?.content_type, slug }
 )
 if (props.thumbnail && thumbnailSrc && !imgError) {
  return <div className={classNames(props.className, 'relative overflow-hidden bg-muted')}>
   <Image src={thumbnailSrc} alt={name || ''} width={150} height={150} sizes="150px" {...(props.priority ? { priority: true } : { loading: 'lazy' as const })} decoding="async" draggable={false} unoptimized={!thumbnailSrc.startsWith('/')}
    className={classNames(props.imageClassName, 'size-full object-cover')}
    onError={() => setImgError(true)} />
  </div>
 }

	return (
		<div
			style={props.style}
			className={classNames(
				{ [(props?.className as any)]: !!props?.className },
				styles.root,
				'relative'
			)}
		>
			{useContentImage && !imgError && (
				<img
					loading="lazy"
					width={64}
					height={64}
					className={classNames(
						{ [(props?.inscriptionClassName as any)]: !!props?.inscriptionClassName },
						styles.inscription
					)}
					src={`${ORD_CONTENT_URL}/${icon_inscription.id}`}
					alt={props?.collection?.name}
					onError={() => setImgError(true)}
				/>
			)}
			{icon_inscription?.id && !isTextRune && !isImageOverrideRune && !hasSlugOverride && !useContentImage && !imgError && (
				<InscriptionContent
					className={classNames(
						{ [(props?.inscriptionClassName as any)]: !!props?.inscriptionClassName },
						styles.inscription
					)}
					inscription={
						// @ts-ignore
						{
							id: icon_inscription?.id,
							// @ts-ignore
							collection: props.collection,
							content_type: icon_inscription?.content_type || 'image/png'
						} as Inscription
					}
				/>
			)}
		{((!icon_inscription?.id && imageSrc) || isImageOverrideRune || hasSlugOverride) && !imgError && (
			<img
				loading="lazy"
				width={64}
				height={64}
				className={classNames(
					{
						[(props?.imageClassName as any)]: !!props?.imageClassName,
						[styles.punks]: name == 'Bitcoin Punks'
					},
					styles.image
				)}
				src={imageSrc}
				alt={props?.collection?.name}
				onError={() => setImgError(true)}
			/>
		)}
		{(imgError || (!icon_inscription?.id && !imageSrc && !isImageOverrideRune && !hasSlugOverride)) && (
			<div className={classNames(props?.className, 'bg-accent w-full h-full flex items-center justify-center overflow-hidden')}>
				{name && (
					<span className="text-[3px] text-muted-foreground leading-none text-center break-all">{name}</span>
				)}
			</div>
		)}
	</div>
	)
}

export default CollectionIcon
